import {
  getMinedexCommodityCategoryFilterDefaults,
  getMinedexSiteTypeDefaults,
  getMinedexTargetCategoryFilterDefaults,
} from '../../minedex/MinedexQueryTool';
import {
  sampleTypeDataPreSelected,
  defaultAssayMArkerColourSettings,
} from '../../soil-assay/AssayQueryTool';
import { defaultDrillHoleColourSettings } from '../../drill-hole/DrillHoleQueryTool';
import { getDefaultTenementTypeList } from '../../tenements/TenementOverlay';

export const DEFAULT_PRESET_ID = '__default__';
export const DEFAULT_PRESET_NAME = 'Default';
export const MAX_USER_PRESETS = 15;
export const PRESET_SCHEMA_VERSION = 1;

const cloneArray = (arr) => arr.map((item) => ({ ...item }));

export function getDefaultPreset() {
  return {
    overlayVisibility: {
      tenements: true,
      tenementLabels: true,
      amalgamationsPending: true,
      soils: true,
      dh: false,
      minedex: false,
      graticles: false,
      // Admin-only overlay. Off by default for everyone, and the checkbox that
      // sets it is not rendered without the admin claim.
      prospect: false,
      // The 10 km block grid reports are commissioned against. Admin only.
      blocks: false,
      // OCR gold values from the local KB GeoServer. Dev/local only.
      ocrGold: false,
      // Its read-confidence checkboxes; absent is treated as shown.
      ocrGoldLow: true,
      ocrGoldMedium: true,
      ocrGoldHigh: true,
      // ASX nugget & alluvial signals. Admin only. Class checkboxes (absent is
      // treated as shown), then the first-release recency window: at most one
      // asxAlluvialWithin* true, none = all.
      asxAlluvial: false,
      asxAlluvialNew: true,
      asxAlluvialRepeat: true,
      asxAlluvialDetecting: true,
      asxAlluvialHistoric: true,
      asxAlluvialWithin6: false,
      asxAlluvialWithin12: false,
      asxAlluvialWithin24: false,
      // Georeferenced map plates from the local GeoServer. Dev/local only.
      asxPlates: false,
      anumberPlates: false,
      section19: true,
      waypoints: true,
      // GSWA faults / shear zones (SLIP). Structure controls lode gold, so this
      // pairs with the geology base maps rather than replacing them.
      waFaults: false,
    },
    layerVisibility: {
      hybrid: true,
      sentinel2Dated: false,
      sentinel2: false,
      topo: false,
      street: false,
      geo100: false,
      geo200: false,
      // GSWA geology (SLIP Geology_and_Soils_Map WMS). NB the WMS layer numbers
      // are the REVERSE of the REST layer ids on the same service - `bedrock`
      // was previously pointing at WMS 15, which is the regolith grid.
      bedrock500: false,     // 1:500 000 State interpreted bedrock geology
      regolith: false,       // Regolith of WA, 500 m grid
      tectonic: false,       // Tectonic units 1:500 000
      magnetic: false,
    },
    // ASTER mineral-map overlays (National ASTER Geoscience Maps, CSIRO/GA via
    // NCI GSKY WMS). These blend on top of the base map (checkbox + opacity),
    // unlike the mutually-exclusive base layers above. Gold-relevant subset.
    asterVisibility: {
      asterFerricOxideContent: false,
      asterFerricOxideComposition: false,
      asterFeOHGroupContent: false,
      asterAlOHGroupContent: false,
      asterAlOHGroupComposition: false,
      asterKaolinGroupIndex: false,
      asterMgOHGroupContent: false,
      asterSilicaIndex: false,
    },
    asterOpacity: 0.75,
    // DEA change/vegetation + interpretive terrain overlays (M4/M5). Additive,
    // like the ASTER group; ids match DEA_OVERLAYS in imagery/sentinelSource.js.
    deaVisibility: {
      deaNdvi: false,
      deaFractionalCover: false,
      deaBareGround: false,
      deaChangeMads: false,
      deaBarestEarth: false,
      deaFerricIron: false,
      deaClayMica: false,
      deaWater: false,
      deaMstp: false,
      deaWeathering: false,
    },
    deaOpacity: 0.8,
    // GeoMAD is the across-the-whole-year change product, so which year (and
    // which stretch) it shows is a real choice, not a fixed layer.
    deaOptions: {
      geomadYear: '2025-01-01',
      geomadStyle: 'tmad_rgb_sens',
    },
    // Terrain. Hillshade only — it is cheap and makes the geology and
    // magnetics rasters read properly. (3D terrain uses the same elevation
    // source and is a small addition when it is wanted.)
    // Land Change — our own derived layer. Its own block rather than a DEA
    // overlay: different source, different service, and it owns a popup.
    landChange: {
      visible: false,
      opacity: 0.85,
    },
    terrain: {
      hillshade: false,
    },
    tenementFilters: {
      includePending: true,
      includeDead: false,
      includeLive: true,
      includeSurveyed: true,
      includeUnsurveyed: true,
      types: getDefaultTenementTypeList(),
      startDate: new Date(1800, 0, 1),
      endDate: new Date(3001, 0, 1),
      grantDate: new Date(1800, 0, 1),
      grantDateBeforeAfter: 'AFTER',
      deadSpls: false,
      grantedOnly: false,
      expiringSoonEnabled: false,
      ignoreFirstTermPl: true,
      overridePendingTenementDisplay: true,
      holderNameLike: '',
    },
    minedexFilters: {
      commodityCategory: getMinedexCommodityCategoryFilterDefaults(),
      targetCommodity: getMinedexTargetCategoryFilterDefaults(),
      siteType: getMinedexSiteTypeDefaults(),
      siteStageUnderDevelopment: true,
      siteStageCareAndMaint: true,
      siteStageOperating: true,
      siteStageShut: true,
      siteStageUndeveloped: true,
      siteStageProposed: true,
    },
    assayFilters: {
      ppblt: 9999,
      ppbmt: 30,
      maxresults: 500,
      type: sampleTypeDataPreSelected,
      nullValues: false,
      anumber: 0,
      bounds: {},
      // Load-date (gswa_load_date) period filter. Off by default so all
      // periods are shown; dateFrom/dateTo are 'YYYY-MM' month strings.
      dateFilterEnabled: false,
      dateFrom: '',
      dateTo: '',
    },
    drillHoleFilters: {
      gptlt: 9999,
      gptmt: 0.1,
      maxDepth: 20,
      anumber: 0,
    },
    assayMarkerColourSettings: cloneArray(defaultAssayMArkerColourSettings),
    drillHoleMarkerColourSettings: cloneArray(defaultDrillHoleColourSettings),
  };
}

export function getDefaultPresetEntry() {
  return {
    id: DEFAULT_PRESET_ID,
    name: DEFAULT_PRESET_NAME,
    version: PRESET_SCHEMA_VERSION,
    createdAt: null,
    updatedAt: null,
    payload: getDefaultPreset(),
    isBuiltIn: true,
  };
}
